// Board navigation bar - the move being looked at, with its verdict, and restarting the line.
//
// The move back / move forward buttons are gone: they sat next to "Take back", which undoes a move
// rather than looking at it, and the two together were unreadable. Looking back is the scoresheet
// (click a move) and the arrow keys, both of which still go through here.

import { store, game } from "../state/index.ts";
import { K } from "../state/keys.ts";
import { live } from "../state/live.ts";
import { jumpToPly, loadLine } from "./board.js";
import * as book from "./book.js";
import { onPositionChanged } from "./positionOrchestrator.js";
import { verdictGlyph, verdictLabel } from "../domain/moveRating.ts";

let navStatus = null;
let onRestart = null;

// Full move history (SAN strings) - snapshotted when at the end
let fullHistory = [];
// Current ply the user is viewing (null = at the end)
let viewPly = null;

export function initBoardNav(elements, callbacks = {}) {
    navStatus = elements.navStatus;
    onRestart = callbacks.onRestart;

    elements.navFirst?.addEventListener("click", goToStart);
    elements.navLast?.addEventListener("click", goToEnd);
    document.getElementById("navRestart")?.addEventListener("click", restartPosition);
    document.getElementById("navFlip")?.addEventListener("click", flipBoard);

    // Keyboard navigation
    document.addEventListener("keydown", (e) => {
        if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA" || e.target.tagName === "SELECT") return;
        if (e.key === "ArrowLeft") { e.preventDefault(); goBack(); }
        if (e.key === "ArrowRight") { e.preventDefault(); goForward(); }
        if (e.key === "Home") { e.preventDefault(); goToStart(); }
        if (e.key === "End") { e.preventDefault(); goToEnd(); }
    });

    // Re-render when a verdict arrives or the practice changes
    store.subscribe('practice.revision', () => updateStatus());
    store.subscribe('board.userSide', () => updateSideLabel());

    updateSideLabel();
}

function restartPosition() {
    if (onRestart && onRestart()) {
        resetView();
        return;
    }

    // No practice on the board: just reload the line's starting position
    const lineFen = store.get('studyLine.baseFen') || store.get(K.boards("study").baseFen);
    loadLine(lineFen, store.get('studyLine.uciMoves') || "");

    store.batch((set) => {
        set('book.latestMoves', []);
        set('book.stats', null);
        set('book.error', null);
        set('myGames.latestMoves', []);
        set('myGames.plans', []);
        set('myGames.moveDiffs', []);
        set('myGames.fen', null);
        set('myGames.stats', null);
        set('myGames.error', null);
    });

    resetView();
    onPositionChanged();
}

/** Forgets the navigation state; called whenever a new line is put on the board. */
export function resetView() {
    fullHistory = [];
    viewPly = null;
    updateStatus();
}

function flipBoard() {
    const ground = live.ground;
    if (!ground) return;
    ground.toggleOrientation();
    const newSide = ground.state.orientation; // "white" or "black"
    store.set('board.userSide', newSide);
    // Reload myGames data since the color filter changed
    book.loadMyMovesForCurrentPosition();
}

function updateSideLabel() {
    const label = document.getElementById("navSideLabel");
    if (!label) return;
    const side = store.get('board.userSide') || 'white';
    label.textContent = side === 'white' ? 'as white' : 'as black';
}

function snapshotHistory() {
    if (viewPly === null) {
        fullHistory = game.history();
    }
}

function currentPly() {
    return viewPly !== null ? viewPly : fullHistory.length - 1;
}

function goToStart() {
    if (fullHistory.length === 0) return;
    viewPly = -1;
    jumpToPly(-1, fullHistory);
    updateStatus();
}

function goBack() {
    if (fullHistory.length === 0) return;
    const cur = currentPly();
    if (cur < 0) return;
    viewPly = cur - 1;
    jumpToPly(viewPly, fullHistory);
    updateStatus();
}

function goForward() {
    if (fullHistory.length === 0) return;
    const cur = currentPly();
    if (cur >= fullHistory.length - 1) return;
    viewPly = cur + 1;
    // The game only remembers the moves up to the position on the board: replay from the snapshot
    jumpToPly(viewPly, fullHistory);
    if (viewPly === fullHistory.length - 1) viewPly = null;
    updateStatus();
}

/**
 * Every move played, whatever position the arrows are looking at. While an earlier one is on the
 * board the game only holds the moves up to it, so whoever needs the whole practice asks here.
 */
export function playedHistory() {
    return viewPly !== null && fullHistory.length ? [...fullHistory] : game.history();
}

/** True while the arrows have the board on an earlier position than the last move played. */
export function isViewingPast() {
    return viewPly !== null;
}

/** Back to the last move played, whatever position is being looked at. */
export function goToEnd() {
    if (fullHistory.length === 0) return;
    viewPly = null;
    jumpToPly(fullHistory.length - 1, fullHistory);
    updateStatus();
}

/**
 * Called externally when a new move is made (resets viewPly to end).
 */
export function onMoveMade() {
    viewPly = null;
    snapshotHistory();
    updateStatus();
}

/** "12. Nf3" / "12... Nf6", numbered from the position the game started from (a line may start at move 8). */
function moveLabelForPly(ply, san) {
    const baseFullmove = store.get(K.boards("study").baseFullmove) || 1;
    const offset = store.get(K.boards("study").baseTurn) === "b" ? ply + 1 : ply;
    const number = baseFullmove + Math.floor(offset / 2);
    return offset % 2 === 0 ? `${number}. ${san}` : `${number}... ${san}`;
}

/**
 * Update the status display: the move being looked at and, for the user's own moves, its verdict.
 */
export function updateStatus() {
    if (!navStatus) return;

    snapshotHistory();

    if (fullHistory.length === 0) {
        navStatus.innerHTML = "";
        return;
    }

    const ply = currentPly();
    if (ply < 0) {
        navStatus.innerHTML = '<span class="board-nav__move-text">Start</span>';
        return;
    }

    const label = moveLabelForPly(ply, fullHistory[ply] || "");
    const move = (store.get('practice.moves') || [])[ply];
    const rated = move && move.actor === "user" && move.verdict.status === "rated";
    const verdictHtml = rated
        ? `<span class="board-nav__verdict" data-verdict="${move.verdict.code}" title="${verdictLabel(move.verdict.code)}">${verdictGlyph(move.verdict.code)}</span>`
        : "";

    navStatus.innerHTML = `<span class="board-nav__move-text">${label}</span>${verdictHtml}`;
}
